import { useCallback, useEffect, useRef, useState } from 'react';
import type { PlaneFinderApp } from '../app/PlaneFinderApp';

/**
 * File loader card + whole-window drag-and-drop, styled after the transcoder
 * harness. A .glb can be dropped ANYWHERE on the window (a full-window highlight
 * appears while dragging); the drop area / "choose file" opens a picker.
 */
export function DropPanel({ app }: { app: PlaneFinderApp }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(
    (file: File) => {
      // Object URL, not FileReader.readAsDataURL: base64 would double the memory of
      // large scans (hundreds of MB) before Babylon even parses. The loader passes an
      // explicit '.glb' extension, so blob: URLs work; revoke once the load settles
      // (loadModel never rejects — failures surface via the 'model-error' event).
      const url = URL.createObjectURL(file);
      void app.loadModel(url, file.name).finally(() => URL.revokeObjectURL(url));
    },
    [app],
  );

  // Whole-window drag-and-drop (not just over the drop area).
  useEffect(() => {
    const onOver = (e: DragEvent) => {
      e.preventDefault();
      setDragging(true);
    };
    const onLeave = (e: DragEvent) => {
      if (e.relatedTarget === null) setDragging(false); // left the window
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const file = e.dataTransfer?.files?.[0];
      if (file && /\.glb$/i.test(file.name)) load(file);
    };
    window.addEventListener('dragover', onOver);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [load]);

  return (
    <>
      {dragging && (
        <div className="pf-dragveil">
          <div className="pf-dragveil-label">Drop a .glb to load</div>
        </div>
      )}

      <div
        className={`drop slot-drop${dragging ? ' drag' : ''}`}
        onClick={() => inputRef.current?.click()}
      >
        <div className="drop-icon">⬇</div>
        <div className="drop-label">Drop a .glb anywhere</div>
        <small>or click to choose a file</small>
        <input
          ref={inputRef}
          type="file"
          accept=".glb,model/gltf-binary"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) load(file);
            e.target.value = '';
          }}
        />
      </div>
    </>
  );
}
